import React from 'react'
import ControllerInput from '../../ControllerFields/ControllerInput'
import I18n from 'APP/I18n'
import ControllerDatePicker from '../../ControllerFields/ControllerDatePicker'
import { useAddDependent } from '../hooks/provider'
import { FORM_FIELDS, TEST_IDS } from '../constants'
import AdultToolTip from '../components/AdultToolTip'
import { FormContainer } from '../styles'
import {
  dateOfBirthValidationRules,
  firstNameValidationRules,
  lastNameValidationRules,
  emailValidationRules,
} from '../validation'
import { useFormFieldVisibility } from '../hooks/useFormFieldVisibility'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { ScrollableItemWrapper, useDynamicScrollIntoView } from 'APP/Hooks/ScrollIntoView'

function AddDependentForm() {
  const { addDependentForm } = useAddDependent()
  const {
    control,
    trigger,
    setValue,
    clearErrors,
    dateOfBirthLabel,
    isChild,
    isAdult,
    focusedField,
    setFocusedField,
  } = addDependentForm

  const {
    handleDatePickerOpen,
    handleDatePickerClose,
    createHandleDatePickerFocus,
    createHandleDatePickerSelect,
    shouldHideChildFields,
    shouldHideAdultFields,
  } = useFormFieldVisibility({ isChild, isAdult })

  const { getItemProps, scrollIntoView } = useDynamicScrollIntoView()
  const handleEmailFocus = () => {
    setFocusedField(FORM_FIELDS.EMAIL)
  }
  const handleEmailPressIn = () => {
    setTimeout(() => {
      // "fast win" solution. The best way is use https://docs.expo.dev/versions/latest/sdk/keyboard-controller/  to control keyboard
      scrollIntoView(FORM_FIELDS.EMAIL)
    }, 350)
  }

  return (
    <FormContainer testID={TEST_IDS.FORM_CONTAINER}>
      <ControllerDatePicker
        scrollViewRef={null}
        control={control}
        name={FORM_FIELDS.DATE_OF_BIRTH}
        testID={TEST_IDS.DATE_OF_BIRTH_PICKER}
        rules={dateOfBirthValidationRules()}
        isFocused={focusedField === FORM_FIELDS.DATE_OF_BIRTH}
        label={dateOfBirthLabel}
        onFocus={createHandleDatePickerFocus(setFocusedField)}
        onSelect={createHandleDatePickerSelect(setFocusedField, trigger)}
        setValue={setValue}
        clearErrors={clearErrors}
        onClose={handleDatePickerClose}
        onOpen={handleDatePickerOpen}
        modalAnimationType="fade"
        placeholder={String(I18n.t('DateFormat'))}
        icon="calendar"
        iconVariant="material"
        {...(isWeb() && { id: 'dobPicker' })}
      />

      {!shouldHideAdultFields && <AdultToolTip />}

      <ControllerInput
        name={FORM_FIELDS.FIRST_NAME}
        testID={TEST_IDS.FIRST_NAME_INPUT}
        control={control}
        rules={firstNameValidationRules(isChild)}
        label={I18n.t('AddDependent.firstName')}
        onFocus={() => setFocusedField(FORM_FIELDS.FIRST_NAME)}
        isFocused={focusedField === FORM_FIELDS.FIRST_NAME}
        hide={shouldHideChildFields}
        id="firstNameInput"
      />

      <ControllerInput
        name={FORM_FIELDS.LAST_NAME}
        testID={TEST_IDS.LAST_NAME_INPUT}
        control={control}
        rules={lastNameValidationRules(isChild)}
        label={I18n.t('AddDependent.lastName')}
        onFocus={() => setFocusedField(FORM_FIELDS.LAST_NAME)}
        isFocused={focusedField === FORM_FIELDS.LAST_NAME}
        hide={shouldHideChildFields}
        id="lastNameInput"
      />

      <ScrollableItemWrapper {...getItemProps(FORM_FIELDS.EMAIL)}>
        <ControllerInput
          name={FORM_FIELDS.EMAIL}
          testID={TEST_IDS.EMAIL_INPUT}
          control={control}
          autoCapitalize="none"
          rules={emailValidationRules(isAdult)}
          label={I18n.t('AddDependent.email')}
          onFocus={handleEmailFocus}
          onPressIn={handleEmailPressIn}
          isFocused={focusedField === FORM_FIELDS.EMAIL}
          hide={shouldHideAdultFields}
          placeholder={I18n.t('AddDependent.emailPlaceholder') ?? ''}
          id="emailInput"
          icon="email-outline"
          iconVariant="material"
        />
      </ScrollableItemWrapper>
    </FormContainer>
  )
}

AddDependentForm.displayName = 'AddDependentForm'

export default AddDependentForm
